import { gql } from '@apollo/client'
import { isEmpty } from 'ramda'
import { useSort } from '@btime/react-contexts'
import React from 'react'

import { serviceOrdersClient } from '../../../../utils/apollo/api'
import { usePaginatedQuery } from '../../../../utils/apollo/_helpers/pagination'
import PaginationControl from '../../../../components/helpers/PaginationControl'
import StatePresenter from '../../../../packages/kiti/components/helpers/StatePresenter'
import { convertFilterToRelativeDates } from '../../../../utils/helpers/date'
import { useProcessesListByIdFilters } from '../../_load/ProcessesListById'

const Context = React.createContext()
const { Provider } = Context
export const useServiceOrdersState = () => React.useContext(Context) || {}
export const useServiceOrders = () => useServiceOrdersState()?.serviceOrders || []

const QUERY = gql`
  query ServiceOrders(
    $page: Int
    $search: String
    $searchType: ServiceOrderSearchType
    $sort: ServiceOrderSort
    $filter: ServiceOrderFilter
  ) {
    serviceOrders(page: $page, search: $search, searchType: $searchType, sort: $sort, filter: $filter) {
      id
      name
      yearlyId
      insertedAt
      endDate
      scheduling
      childrenCount
      priority {
        id
        name
      }
      status {
        id
        name
        label
        color
      }
      checklist {
        id
        name
      }
      place {
        id
        name
        resume
      }
      asset {
        id
        name
        type {
          id
          name
        }
      }
      group {
        id
        name
      }
      groups {
        id
        name
      }
      user {
        id
        name
      }
    }
  }
`

export function groupProcessesFilters(data) {
  if (!data) return {}

  const validData = data.filter(item => item && Object.keys(item).length > 0)
  if (validData.length === 0) return {}

  return validData.reduce((acc, item) => {
    Object.entries(item).forEach(([key, value]) => {
      if (!acc[key]) {
        acc[key] = []
      }

      if (Array.isArray(value)) {
        const isArrayOfObjectsWithId = value.length > 0 && typeof value[0] === 'object' && value[0].id !== undefined

        if (isArrayOfObjectsWithId) {
          const ids = value.map(obj => obj.id)
          acc[key] = [...new Set([...acc[key], ...ids])]
        } else {
          acc[key] = [...new Set([...acc[key], ...value])]
        }
      } else if (typeof value === 'string' && value) {
        if (!acc[key].includes(value)) {
          acc[key].push(value)
        }
      }
    })

    return acc
  }, {})
}

const ServiceOrdersLoader = ({
  children,
  search,
  searchType,
  filter,
  defaultSort = { field: 'ID', type: 'DESC' },
  uncontrolledChildren
}) => {
  const [page, setPage] = React.useState(1)
  const { field, type } = useSort()
  const { field: defaultField, type: defaultType } = defaultSort

  const processesFilters = useProcessesListByIdFilters()

  filter = groupProcessesFilters([...processesFilters, convertFilterToRelativeDates(filter)])

  if (isEmpty(filter) && !search) {
    filter = { ...filter, with_parent: false }
  }

  const { data, refetch, fetchMore, ...state } = usePaginatedQuery(QUERY, {
    variables: {
      page, //
      search,
      searchType: !!searchType && !!search ? searchType : undefined,
      sort: { field: field || defaultField, type: type || defaultType },
      filter
    },
    flexiblePollInterval: 25000,
    client: serviceOrdersClient
  })

  return (
    <Provider value={data}>
      <StatePresenter {...state} uncontrolledChildren={uncontrolledChildren}>
        {children}
      </StatePresenter>
      <PaginationControl fetchMore={fetchMore} page={page} onChange={setPage} state={state} />
    </Provider>
  )
}

export default ServiceOrdersLoader
